<template>
  <div
    class="fapi-form-prices f-flex f-flex-col f-pt-1 f-px-2 f-rounded f-opacity-100"
    :key="currency"
  >
    <div
      v-if="discountCode && discountCode.isPercent() && discount > 0"
      class="fapi-form-prices-discount f-flex f-justify-between f-items-baseline"
    >
      <div
        class="fapi-form-prices-discount-label f-text-sm f-min-w-max f-text-right f-mr-4"
      >
        {{ $t("result.prices.discountPrice") }}:
      </div>
      <div class="fapi-form-prices-discount-value f-text-sm">
        {{ $n(discountCode.getPercentDiscountFraction(), "percent") }}
      </div>
    </div>
    <div
      v-if="NumberHelper.round(totalDiscount, 2) > 0"
      class="fapi-form-prices-total-discount f-flex f-justify-between f-items-baseline"
    >
      <div
        class="fapi-form-prices-total-discount-label f-text-sm f-min-w-max f-text-right f-mr-4"
      >
        {{ $t("result.prices.totalDiscountPrice") }}:
      </div>
      <div class="fapi-form-prices-total-discount-value f-text-sm">
        <span v-if="repayment">{{ repaymentCount }} x</span>
        {{ $n(totalDiscount, "currency") }}
      </div>
    </div>

    <div
      class="fapi-form-prices-repayment-wo-vat f-flex f-justify-between f-items-baseline"
      v-if="isVatPayer"
    >
      <div
        class="fapi-form-prices-repayment-wo-vat-label f-text-sm f-min-w-max f-text-right f-mr-4"
      >
        {{
          repayment
            ? $t("result.prices.firstRepaymentTotalToPayWithOutVat")
            : $t("result.prices.totalWithOutVat")
        }}:
      </div>
      <div class="fapi-form-prices-repayment-wo-vat-value">
        {{
          repayment
            ? $n(
                installments[0].installmentAmount + disableRepaymentTotalPrice,
                "currency",
              )
            : $n(totalPrice, "currency")
        }}
      </div>
    </div>

    <div
      class="fapi-form-prices-repayment-with-vat f-flex f-justify-between f-items-baseline"
    >
      <div
        class="fapi-form-prices-repayment-with-vat-label f-text-sm f-min-w-max f-text-right f-mr-4"
      >
        {{
          repayment
            ? $t("result.prices.firstRepaymentTotalToPay")
            : $t("result.prices.totalToPay")
        }}:
      </div>
      <div class="fapi-form-prices-repayment-with-vat-value f-text-xl">
        {{
          repayment
            ? $n(
                installments[0].installmentAmountIncVat +
                  disableRepaymentTotalPriceIncVat,
                "currency",
              )
            : $n(totalPriceIncludingVat, "currency")
        }}
      </div>
    </div>

    <div v-if="repayment" class="fapi-form-prices-repayment-list f-mt-4">
      <button
        type="button"
        @click="toggleInstallments"
        class="fapi-form-prices-repayment-list-button f-btn f-btn-primary"
      >
        {{
          showInstallments
            ? isChangeNameRepaymentToPayment
              ? $t("result.prices.payment_overview_off")
              : $t("result.prices.repayment_overview_off")
            : isChangeNameRepaymentToPayment
              ? $t("result.prices.payment_overview_on")
              : $t("result.prices.repayment_overview_on")
        }}
      </button>

      <div
        v-if="showInstallments"
        class="fapi-form-prices-repayment-list-view f-mt-4"
      >
        <div
          v-for="index in repaymentCount"
          :key="index"
          :class="[
            'fapi-form-prices-repayment-list-view-index',
            'fapi-form-prices-repayment-list-view-index-' + index,
            'f-flex',
            'f-justify-between',
            'f-items-baseline',
          ]"
        >
          <div
            :class="[
              'fapi-form-prices-repayment-list-view-label',
              'fapi-form-prices-repayment-list-view-label-' + index,
              'f-text-sm',
              'f-min-w-max',
              'f-text-right',
              'f-mr-4',
            ]"
          >
            {{ index }}.
            {{
              isChangeNameRepaymentToPayment
                ? $t("result.prices.payment")
                : $t("result.prices.repayment")
            }}:
          </div>
          <div
            :class="[
              'fapi-form-prices-repayment-list-view-value',
              'fapi-form-prices-repayment-list-view-value-' + index,
              'f-text-sm',
            ]"
          >
            <span v-if="index === 1 && disableRepaymentTotalPrice > 0">
              {{
                $n(
                  installments[index - 1].installmentAmountIncVat +
                    disableRepaymentTotalPriceIncVat,
                  "currency",
                )
              }}
            </span>
            <span v-else>
              {{
                $n(installments[index - 1].installmentAmountIncVat, "currency")
              }}
            </span>
          </div>
        </div>
        <div
          class="fapi-form-prices-total-to-pay f-flex f-justify-between f-items-baseline"
        >
          <div
            class="fapi-form-prices-total-to-pay-label f-text-sm f-min-w-max f-text-right f-mr-4"
          >
            {{ $t("result.prices.repaymentTotalToPay") }}:
          </div>
          <div class="fapi-form-prices-total-to-pay-value f-text-xl">
            {{
              $n(
                totalInstallment + disableRepaymentTotalPriceIncVat,
                "currency",
              )
            }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import OrderPrices from "@/module/Order/OrderPrices";
import { isFapiDiscountCodeOrNull } from "@/module/ComponentPropsValidations/validations";
import NumberHelper from "@/module/NumberHelper";
import FormHelper from "@/module/FormHelper";

export default Vue.extend({
  name: "Prices",

  props: {
    order: {
      type: Object as PropType<OrderPrices>,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    discountCode: {
      required: true,
      validator: isFapiDiscountCodeOrNull,
    },
    repayment: {
      type: Boolean,
      required: true,
    },
    repaymentCount: {
      type: Number,
      required: true,
    },
    showPricesIncludingVat: {
      type: Boolean,
      required: true,
    },
    isChangeNameRepaymentToPayment: {
      type: Boolean,
      required: true,
    },
    installments: {
      type: Array as PropType<InstallmentItem[]>,
      required: true,
    },
  },

  data() {
    return {
      showInstallments: false,
      fractions: [] as number[],
    };
  },

  computed: {
    NumberHelper() {
      return NumberHelper;
    },
    discount(): number {
      return this.order.discount;
    },

    totalDiscount(): number {
      if (this.repayment) {
        if (this.showPricesIncludingVat) {
          return (
            (this.order.originalPriceIncVat -
              (this.totalPriceIncludingVat +
                this.disableRepaymentTotalPriceIncVat)) /
            this.repaymentCount
          );
        }

        return (
          (this.order.originalPrice -
            (this.totalPrice + this.disableRepaymentTotalPrice)) /
          this.repaymentCount
        );
      }

      if (this.showPricesIncludingVat) {
        return this.order.originalPriceIncVat - this.totalPriceIncludingVat;
      }

      return this.order.originalPrice - this.totalPrice;
    },

    totalPrice(): number {
      return this.order.totalPrice;
    },

    totalPriceIncludingVat(): number {
      return this.order.totalPriceIncludingVat;
    },

    disableRepaymentTotalPriceIncVat(): number {
      return this.order.disableRepaymentTotalPriceIncVat;
    },

    disableRepaymentTotalPrice(): number {
      return this.order.disableRepaymentTotalPrice;
    },

    isVatPayer(): boolean {
      return this.order.isVatPayer;
    },

    totalInstallment(): number {
      return this.installments.reduce(
        (sum, installment) => sum + installment.installmentAmountIncVat,
        0,
      );
    },
  },

  methods: {
    toggleInstallments() {
      this.showInstallments = !this.showInstallments;
    },
  },

  mounted() {
    this.fractions = FormHelper.createFractionMap(this.repaymentCount);
  },
});
</script>

<style lang="scss"></style>
